import { NavLink } from 'react-router-dom';
import { buildCitation } from '../citation-format';
import type { DocumentRow } from '../types';
import { DocumentFlagList } from './document-flags';
import { money } from './expand/common';
import { cardTitleText } from './utils';
import styles from './document-card.module.scss';

interface DocumentLinkTextProps {
  href?: string | null;
  documentLink: (href: string) => string;
  className?: string;
  children: string;
}

/**
 * Text that opens the document page in a new tab when the document has a page,
 * else plain text. Clicks stop so they don't toggle the card.
 * @param {DocumentLinkTextProps} props The link.
 * @return {JSX.Element} The link or text.
 */
function DocumentLinkText({
  href,
  documentLink,
  className,
  children,
}: DocumentLinkTextProps) {
  if (!href) return <span className={className}>{children}</span>;
  return (
    <NavLink
      to={documentLink(href)}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </NavLink>
  );
}

/**
 * The AFE well name and basin · formation under the title.
 * @param {object} props The block.
 * @param {DocumentRow['afeSummary']} props.summary The AFE summary.
 * @return {JSX.Element | null} The block, or nothing without well info.
 */
function AfeWellInfo({ summary }: { summary: DocumentRow['afeSummary'] }) {
  if (!summary || !(summary.wellName || summary.basin || summary.formation)) {
    return null;
  }
  return (
    <div className={styles.afeWellInfo}>
      {summary.wellName && (
        <div className={styles.afeWellName}>{summary.wellName}</div>
      )}
      {(summary.basin || summary.formation) && (
        <div className={styles.afeWellMeta}>
          {[summary.basin, summary.formation].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
}

/**
 * The royalty check's number and total under the title.
 * @param {object} props The block.
 * @param {DocumentRow} props.document The document.
 * @return {JSX.Element | null} The block, or nothing without check info.
 */
function RcCheckInfo({ document }: { document: DocumentRow }) {
  const { rcSummary, checkTotal } = document;
  const hasTotal = checkTotal !== null && checkTotal !== undefined;
  if (!rcSummary || !(rcSummary.checkNumber || hasTotal)) return null;
  return (
    <div className={styles.afeWellInfo}>
      {rcSummary.checkNumber && (
        <div className={styles.rcCheckNumber}>
          Check #{rcSummary.checkNumber}
        </div>
      )}
      {hasTotal && (
        <div className={styles.afeWellMeta}>{money(checkTotal)}</div>
      )}
    </div>
  );
}

interface DocumentCardTitleProps {
  document: DocumentRow;
  documentLink: (href: string) => string;
  hasExpand: boolean;
}

/**
 * The card's title column: title, document link, citation, flags and the
 * AFE/RC summary lines.
 * @param {DocumentCardTitleProps} props The column.
 * @return {JSX.Element} The column.
 */
export function DocumentCardTitle({
  document,
  documentLink,
  hasExpand,
}: DocumentCardTitleProps) {
  const { citation, citationFull } = buildCitation(
    document.volume,
    document.page,
    document.instrumentNumber
  );
  return (
    <div className={styles.titleBlock}>
      <div className={styles.titleRow}>
        <span className={styles.title}>{cardTitleText(document)}</span>
      </div>
      {document.title && (
        <DocumentLinkText
          href={document.href}
          documentLink={documentLink}
          className={styles.heading}
        >
          {document.title}
        </DocumentLinkText>
      )}
      {citation && citationFull !== document.title && (
        <span className={styles.citation}>
          <span className={styles.citationDot} />
          <DocumentLinkText href={document.href} documentLink={documentLink}>
            {citationFull}
          </DocumentLinkText>
        </span>
      )}
      {document.flags && <DocumentFlagList flags={document.flags} />}
      <AfeWellInfo summary={document.afeSummary} />
      <RcCheckInfo document={document} />
      {hasExpand && <span className={styles.more}>…</span>}
    </div>
  );
}
